/**
 * StepShell.tsx
 *
 * Standardized wrapper for wizard step content.
 *
 * Layout:
 *   ┌─ .shell ──────────────────────────────────────────────────┐
 *   │  .header   — title + optional subtitle                    │
 *   │  .body     — children (flex: 1, overflow-y: auto)         │
 *   │  .footer   — right-aligned next/continue button           │
 *   └───────────────────────────────────────────────────────────┘
 *
 * When loading=true, body is replaced with skeleton placeholders and
 * a centered loadingMessage text.
 */

import { Skeleton, Button } from '@/components/ui';
import styles from './StepShell.module.css';

interface StepShellProps {
  /** Step body content. */
  children: React.ReactNode;
  /** Step heading shown at the top. */
  title: string;
  /** Optional sub-heading shown below the title. */
  subtitle?: string;
  /** Label for the next/continue button. Defaults to "Continue". */
  nextLabel?: string;
  /** Called when the next button is clicked. If omitted, no button is rendered. */
  onNext?: () => void;
  /** Disables the next button without hiding it. */
  nextDisabled?: boolean;
  /** When true, shows a skeleton loading state instead of children. */
  loading?: boolean;
  /** Message shown below the skeletons during the loading state. */
  loadingMessage?: string;
  /** Max content width in px. Defaults to 1100. */
  maxWidth?: number;
}

export const StepShell = ({
  children,
  title,
  subtitle,
  nextLabel = 'Continue',
  onNext,
  nextDisabled = false,
  loading = false,
  loadingMessage,
  maxWidth = 1100,
}: StepShellProps) => {
  return (
    <div className={styles.shell}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className={styles.header} style={{ maxWidth }}>
        <h2 className={styles.title}>{title}</h2>
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
      </div>

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <div className={styles.body} style={{ maxWidth }}>
        {loading ? (
          <div className={styles.loadingState} aria-busy aria-label={loadingMessage ?? 'Loading...'}>
            <Skeleton variant="rect" height={24} />
            <Skeleton variant="rect" height={20} />
            <Skeleton variant="rect" height={20} />
            <Skeleton variant="rect" height={16} />
            {loadingMessage && (
              <p className={styles.loadingMessage}>{loadingMessage}</p>
            )}
          </div>
        ) : (
          children
        )}
      </div>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      {onNext && (
        <div className={styles.footer} style={{ maxWidth }}>
          <Button
            variant="primary"
            size="lg"
            onClick={onNext}
            disabled={nextDisabled || loading}
          >
            {nextLabel}
          </Button>
        </div>
      )}
    </div>
  );
};
